import React, { useContext, useEffect, useRef, useState } from 'react';
import { Modal, BasicModalProps } from '../../core/modal';
import { SignAgreement, SignAgreementProps } from '.';
import { fetchAgreementPreview as fetchAgreementPreviewService } from './service';
import styles from './sign_agreement.module.scss';
import Bundle from '../../../models/Bundle';
import CreditCheckResult from '../../../models/CreditCheckResult';
import { AgreementSignee, Agreement, AgreementSigningMethod } from '../../../models/Agreement';
import { AgreementMetadata, AgreementStatus } from '../../../models/AgreementMetadata';
import { SalesContext } from '../../../SalesContext';
import Product from '../../../models/Product';

type Props = {
  creditCheckResult: CreditCheckResult;
  bundles: Bundle[];
  signingMethod?: AgreementSigningMethod,
  photo?: Blob;
  shouldFetch: boolean;
  shouldRenderPdf: boolean;
} & BasicModalProps & Pick<SignAgreementProps, 'onSigned'>;

/**
 * Modal for signing an agreement.
 */
export const SignAgreementModal: React.FC<Props> = ({
  creditCheckResult,
  bundles,
  photo,
  onSigned,
  signingMethod,
  shouldFetch,
  shouldRenderPdf,
  ...props
}) => {
  const { agreement: createdAgreement, monthlyPaymentTotal } = useContext(SalesContext);

  const [agreementUri, setAgreementUri] = useState<string>(createdAgreement?.uri);

  const subscriptions: Product[] = [];
  bundles.forEach(bundle => {
    const productsWithContactInfo = bundle.energyItems.map(item => {
      return {
        ...item,
        address: bundle.address,
        city: bundle.city,
        postalCode: bundle.postalCode
      }
    })
    subscriptions.push(...productsWithContactInfo)
  })

  async function fetchAgreementPreview() {
    if (!creditCheckResult || !bundles) {
      throw new Error("Non-ok creditCheckResult or bundles");
    }

    try {
      var blob = await fetchAgreementPreviewService({
        signee: new AgreementSignee({ ...creditCheckResult }),
        subscriptions,
        bundles,
        photo,
        monthlyPaymentTotal
      }, shouldRenderPdf ? "pdf" : "image");

      setAgreementUri(URL.createObjectURL(blob));
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    if (createdAgreement?.uri) {
      setAgreementUri(createdAgreement?.uri);
    }

    if (!props.open) {
      return;
    }

    const timer = setTimeout(() => {
      fetchAgreementPreview();
    }, 100);

    return () => clearTimeout(timer);
  }, [createdAgreement, props.open, shouldFetch]);

  return (
    <Modal {...props} contentClassName={styles.content}>
      <SignAgreement
        subscriptions={subscriptions}
        meta={new AgreementMetadata({
          subscriptions,
          isManualSignatureRequired: signingMethod === AgreementSigningMethod.Manual,
          status: AgreementStatus.Opened,
          creditCheckResult,
          bundles,
        })}
        agreement={{
          key: createdAgreement?.key || null,
          uri: agreementUri
        }}
        onSigned={onSigned}
        usePdfRenderer={shouldRenderPdf}
        listEnergySummary
        photo={photo}
      />
    </Modal>
  )
}